import { ResolutionType, useScreenStore } from "application/contexts/screen/ScreenStore";
import { useTranslation } from "i18n/useTranslation";
import React, { FC } from "react";
import { Keyboard } from "components/keyboard/Keyboard";
import { numberMoneyMask } from "corss-cutting/masks/money";
import { IFullCardData } from "domains/cashless/aggregates/ICardCashless";
import { Kiosk } from "domains/kiosk/aggregates/Kiosk";
import { TransactionType } from "domains/order/agreggates/payment/transactionType";
import { SelectCardType } from "areas/cashless/offlineRecharge/components/selectCardType/SelectCardType";
import styles from "../../../offlineRecharge/OfflineRecharge.module.scss";

/**
 * Etapa 2: Inserir valor da recarga + forma de pagamento.
 * Exibe documento do cartão, saldo, valor de ativação (se abertura), teclado, valores rápidos e SelectCardType.
 */
export interface RechargeCardLayoutProps {
  cardData: IFullCardData;
  isClosed: boolean;
  rechargeValueCents: number;
  activationDisplayValue: number;
  documentDisplay: string;
  kiosk: Kiosk | null;
  onAddQuickValue: (reais: number) => void;
  onKeyPress: (key: string) => void;
  onClearValue: () => void;
  onPaymentSelect: (transactionType: TransactionType) => void;
}

export const RechargeCardLayout: FC<RechargeCardLayoutProps> = ({
  cardData,
  isClosed,
  rechargeValueCents,
  activationDisplayValue,
  documentDisplay,
  kiosk,
  onAddQuickValue,
  onKeyPress,
  onClearValue,
  onPaymentSelect,
}) => {
  const { resolution } = useScreenStore();
  const { t } = useTranslation();

  return (
    <>
      {cardData?.Document && (
        <p
          className={styles.cpf}
          style={{
            margin: resolution === ResolutionType.FULL_HD ? "36px 0" : "12px 0",
          }}
        >
          {documentDisplay}
        </p>
      )}
      <div className={styles.balance}>
        <p
          style={{
            fontSize: resolution === ResolutionType.FULL_HD ? 30 : 26,
          }}
        >
          {t("offline_recharge.pay_recharge.balance")}
        </p>
        <p
          style={{
            fontSize: resolution === ResolutionType.FULL_HD ? 80 : 60,
          }}
        >
          {numberMoneyMask(cardData.Balance / 100)}
        </p>
      </div>

      <div
        className={styles.rechargeValue}
        style={{
          margin: resolution === ResolutionType.FULL_HD ? 48 : 0,
        }}
      >
        {isClosed && activationDisplayValue > 0 && (
          <p
            className={styles.activationCost}
            style={{
              marginBottom: resolution === ResolutionType.FULL_HD ? 16 : 12,
              fontSize: resolution === ResolutionType.FULL_HD ? 28 : 22,
            }}
          >
            {t("offline_recharge.pay_recharge.activation_cost")}:{" "}
            {numberMoneyMask(activationDisplayValue)}
          </p>
        )}
        <div
          className={styles.previousValueContainer}
          style={{
            padding: resolution === ResolutionType.FULL_HD ? "0 24px" : "0 12px",
            marginBottom: resolution === ResolutionType.FULL_HD ? 50 : 30,
          }}
        >
          {[10, 30, 50, 100].map((reais) => (
            <div
              key={reais}
              style={{
                padding:
                  resolution === ResolutionType.FULL_HD
                    ? "24px 36px"
                    : "20px",
                fontSize: resolution === ResolutionType.FULL_HD ? 20 : 16,
              }}
              onClick={() => onAddQuickValue(reais)}
            >
              {`+ ${numberMoneyMask(reais)}`}
            </div>
          ))}
        </div>
        <div
          className={styles.rechargeValueContainer}
          style={{
            margin: resolution === ResolutionType.FULL_HD ? 24 : 16,
            marginBottom: resolution === ResolutionType.FULL_HD ? 80 : 40,
          }}
        >
          <div
            className={
              rechargeValueCents > 0 ? styles.value : styles.placeholder
            }
            style={{
              fontSize: resolution === ResolutionType.FULL_HD ? 45 : 30,
            }}
          >
            {rechargeValueCents > 0
              ? numberMoneyMask(rechargeValueCents / 100)
              : t("offline_recharge.pay_recharge.placeholder")}
          </div>
        </div>
        <div className={styles.keyboardContainer}>
          <Keyboard
            type="clear"
            hideClear
            onPressBack={onClearValue}
            onKeyPress={onKeyPress}
          />
        </div>
      </div>

      <div className={styles.paymentForm}>
        <div>
          {kiosk && (
            <SelectCardType
              kiosk={kiosk}
              onSelect={onPaymentSelect}
              rechargePayment
            />
          )}
        </div>
      </div>
    </>
  );
};
